import React, { useState, memo, useCallback } from "react";
import { copyToClipboard } from "@/utils/clipboard";
import { Post, PostStatus } from "@/core/types/postTypes";
import { PublishedPostStats } from "@/components/PostCard";
import type { StatsLocale } from "@/components/PostCard";
import { PostMediaThumbnail } from "./PostMediaThumbnail";
import { DeletePostButton } from "../DeletePostButton";
import { DownloadPostButton } from "../DownloadPostButton";
import { PostPreparationBadge } from "../components/PostPreparationBadge";
import { usePostModals } from "../hooks/usePostModals";
import { useCaptions } from "../../../hooks/useCaptions";
import { useCaptionGenerationStore } from "../hooks/useCaptionGenerationStore";
import { usePostCardAuthOptional } from "../contexts/PostCardAuthContext";
import { UserRole } from "@/services/urlService";
import { postCacheUtils } from "../hooks/usePostsQuery";
import { CaptionGeneratorModal } from "../components/CaptionGeneratorModal";
import { ManualCaptionModal } from "../components/ManualCaptionModal";
import {
  GlobeIcon,
  SpinnerIcon,
  BoltIcon,
  CopyIcon,
  CheckIcon,
  EditIcon,
} from "@/components/icons";
import { useAppStore } from "@/core/store/useAppStore";
import { LinkedResources } from "@/components/ui/LinkedResources";
import { usePostMedia } from "./usePostMedia";

interface ListPostCardProps {
  post: Post;
  onStatusChange: (post: Post, newStatus: PostStatus, response: unknown) => void;
  onPostDeleted: () => void;
  /** Per-platform permalink overrides forwarded to the stats panel so
   *  the FB/IG/TikTok badges become real "view on platform" links. Same
   *  pipeline used by EditorPostCard in the reporting grid. */
  statsPermalinks?: Partial<Record<string, string | null | undefined>>;
  /** Copy locale for the embedded stats panel column headers. */
  locale?: StatsLocale;
}

/**
 * "Ledger entry" list row — a horizontal published-post card sized to
 * make the lifetime metrics panel the visual anchor of the right side.
 * Composes the same PublishedPostStats used by the grid card and the
 * reporting view, so the three views read as variations on a single
 * design language.
 *
 * Used by:
 *   - PostsView.tsx in list mode (admin)
 *   - Client portal list-style routes (read-only; admin-only actions
 *     gate on UserRole === ADMIN via PostCardAuthContext).
 */
export const ListPostCard: React.FC<ListPostCardProps> = memo(({
  post,
  onStatusChange,
  onPostDeleted,
  statsPermalinks,
  locale = "en",
}) => {
  const { imageCount, authContext, selectedClient } = usePostMedia(post);

  const { openEditModal, openReelModal } = usePostModals();
  const { error: captionError } = useCaptions();
  const { captions } = useAppStore();

  const [copiedPostId, setCopiedPostId] = useState(false);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [showManualCaptionModal, setShowManualCaptionModal] = useState(false);
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);

  const isAdmin = authContext?.userRole !== UserRole.CLIENT;
  const isPublishedStatus = post.status === PostStatus.PUBLISHED ||
    post.status === PostStatus.PARTIALLY_PUBLISHED;
  const isReel = post.postType === "reel";
  const isStory = post.postType === "story";

  const activeGridPosition = post.activeGridPosition;
  const activeGridTotal = post.activeGridTotal;

  const generationState = useCaptionGenerationStore((state) => {
    const gen = state.generations[post.id];
    if (!gen) return null;
    const STALE_MS = 5 * 60 * 1000;
    if (gen.status === "generating" && (Date.now() - gen.startedAt) > STALE_MS) return null;
    return gen;
  });

  const captionData = captions[post.id] || {
    caption: null, isLoading: false, error: null, lastGenerated: null,
  };

  const handleEditPost = useCallback(() => {
    if (isReel) openReelModal(post);
    else openEditModal(post);
  }, [openEditModal, openReelModal, post, isReel]);

  const handleGenerateCaption = useCallback(() => {
    if (!selectedClient) return;
    setShowCaptionModal(true);
  }, [selectedClient]);

  const handleMakePostPublic = useCallback(async () => {
    if (isTogglingPublic || !selectedClient) return;
    setIsTogglingPublic(true);
    try {
      const { apiClient } = await import("@/services/apiClient");
      await apiClient.post(`/client/${selectedClient.id}/posts/${post.id}/make-public`, undefined, { timeout: 120000 });
      postCacheUtils.invalidateAllPosts(selectedClient.id);
    } catch (err) {
      console.error("Failed to make post public:", err);
    } finally {
      setIsTogglingPublic(false);
    }
  }, [isTogglingPublic, selectedClient, post.id]);

  const handleRemovePostPublic = useCallback(async () => {
    if (isTogglingPublic || !selectedClient) return;
    setIsTogglingPublic(true);
    try {
      const { apiClient } = await import("@/services/apiClient");
      await apiClient.delete(`/client/${selectedClient.id}/posts/${post.id}/make-public`);
      postCacheUtils.invalidateAllPosts(selectedClient.id);
    } catch (err) {
      console.error("Failed to remove post public:", err);
    } finally {
      setIsTogglingPublic(false);
    }
  }, [isTogglingPublic, selectedClient, post.id]);

  const handleCopyId = useCallback(() => {
    copyToClipboard(post.id);
    setCopiedPostId(true);
    setTimeout(() => setCopiedPostId(false), 2000);
  }, [post.id]);

  // ---- Visual tokens for the post type (drives the accent rail and
  //      the corner pill). Explicit map so Tailwind's JIT keeps the
  //      class names alive.
  const railClass = isReel
    ? "bg-linear-to-b from-emerald-500/60 via-emerald-500/15 to-transparent"
    : isStory
      ? "bg-linear-to-b from-amber-500/60 via-amber-500/15 to-transparent"
      : "bg-linear-to-b from-blue-500/55 via-blue-500/15 to-transparent";
  const typePillClass = isReel
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200/80"
    : isStory
      ? "bg-amber-50 text-amber-700 ring-amber-200/80"
      : "bg-blue-50 text-blue-700 ring-blue-200/80";

  // Compact eyebrow line: "PUB · 21 MAY 2026 · 5 IMGS"
  const eyebrow = buildEyebrow({
    publishedAt: post.publishedAt ?? post.createdAt,
    isReel,
    isCarousel: !!post.isCarousel,
    imageCount,
    locale,
  });

  return (
    <div
      className={[
        "group relative overflow-hidden rounded-2xl",
        "bg-linear-to-r from-white via-white to-gray-50/40",
        "ring-1 ring-gray-200/70",
        "transition-all duration-200",
        "hover:ring-gray-300 hover:shadow-[0_10px_30px_-18px_rgb(0_0_0/0.18)]",
      ].join(" ")}
    >
      {/* Vertical accent rail — color-coded by post type. Adds a tiny
          "ledger tab" that punctuates the list rhythm without
          competing with the content. */}
      <span
        aria-hidden
        className={`absolute inset-y-0 left-0 w-[3px] ${railClass}`}
      />

      <div className="flex items-stretch gap-5 p-4 pl-[18px]">
        {/* ---- Thumbnail ---- */}
        <PostMediaThumbnail
          post={post}
          bottomLeftOverlay={
            activeGridPosition != null && activeGridTotal != null ? (
              <span
                className="px-1.5 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded-full shadow-md tabular-nums"
                title="Active grid position"
              >
                #{activeGridTotal - activeGridPosition}
              </span>
            ) : null
          }
        />

        {/* ---- Middle column: eyebrow / caption / meta chips ---- */}
        <div className="flex flex-col min-w-0 flex-1 gap-2 py-0.5">
          {/* Eyebrow */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={[
                "inline-flex items-center px-2 py-0.5 rounded-md ring-1",
                "text-[9px] font-bold uppercase tracking-[0.12em]",
                typePillClass,
              ].join(" ")}
            >
              {isReel ? "Reel" : isStory ? "Story" : "Post"}
            </span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-gray-400">
              {eyebrow}
            </span>
          </div>

          {/* Caption — italic to mark this as creative content vs the
              numeric stats panel on the right. line-clamp-2 keeps row
              heights consistent across the grid. */}
          <p
            className={[
              "text-[13px] leading-snug line-clamp-2",
              post.caption
                ? "italic text-gray-700"
                : "text-gray-400",
            ].join(" ")}
          >
            {post.caption || "No caption"}
          </p>

          {/* Meta chip row */}
          <div className="mt-auto flex items-center gap-1.5 flex-wrap">
            {/* Copy ID — subtle, behaves as a pill rather than a button */}
            <button
              onClick={handleCopyId}
              className={[
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md",
                "text-[10px] font-medium tabular-nums",
                "ring-1 transition-colors",
                copiedPostId
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200/80"
                  : "bg-gray-50 text-gray-500 ring-gray-200/80 hover:bg-gray-100 hover:text-gray-700",
              ].join(" ")}
              title={copiedPostId ? "Copied!" : `Copy ID: ${post.id}`}
            >
              {copiedPostId ? (
                <CheckIcon className="w-3 h-3" />
              ) : (
                <CopyIcon className="w-3 h-3" />
              )}
              <span className="font-mono">{post.id.slice(-6)}</span>
            </button>

            {/* Commercial flag */}
            {post.isCommercial && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 ring-1 ring-amber-200/80 text-[10px] font-medium rounded-md"
                title="Flagged for auto-boost consideration"
              >
                <BoltIcon className="w-3 h-3" />
                Commercial
              </span>
            )}

            {/* Public sharing toggle — admin only */}
            {isPublishedStatus && isAdmin && (
              post.publicStatus === "copying" || isTogglingPublic ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-50 text-yellow-700 ring-1 ring-yellow-200/80 text-[10px] font-medium rounded-md">
                  <SpinnerIcon className="w-3 h-3" />
                  Copying…
                </span>
              ) : post.publicStatus === "public" ? (
                <button
                  onClick={handleRemovePostPublic}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80 text-[10px] font-medium rounded-md hover:bg-red-50 hover:text-red-600 hover:ring-red-200 transition-colors"
                  title="Click to remove public link"
                >
                  <GlobeIcon className="w-3 h-3" />
                  Public
                </button>
              ) : (
                <button
                  onClick={handleMakePostPublic}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 text-gray-500 ring-1 ring-gray-200/80 text-[10px] font-medium rounded-md hover:bg-emerald-50 hover:text-emerald-700 hover:ring-emerald-200 transition-colors"
                  title="Make public"
                >
                  <GlobeIcon className="w-3 h-3" />
                  Make public
                </button>
              )
            )}

            {/* Preparation badge — only shown when prep is pending /
                failed; "ready" state stays silent. Admin-only signal. */}
            {isAdmin && <PostPreparationBadge post={post} clientId={authContext?.clientId} />}

            {/* Download pill — admin only */}
            {isAdmin && <DownloadPostButton post={post} pill />}

            {/* Client-reviewed pill — visible to both sides as a status
                signal, in Italian to match the rest of the portal UI. */}
            {post.clientReviewedAt && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80 text-[10px] font-medium rounded-md"
                title={`Approvato ${new Date(post.clientReviewedAt).toLocaleDateString("it-IT")}`}
              >
                <CheckIcon className="w-3 h-3" />
                Cliente OK
              </span>
            )}

            <LinkedResources entityType="post" entityId={post.id} className="!mt-0" />
          </div>
        </div>

        {/* ---- Stats panel (right side anchor) ---- */}
        {isPublishedStatus && (
          <div className="hidden lg:block w-[360px] shrink-0 self-center">
            <PublishedPostStats
              metricsByPlatform={post.metricsByPlatform}
              permalinks={statsPermalinks}
              locale={locale}
              className="!mt-0"
            />
          </div>
        )}

        {/* ---- Vertical action stack (admin-only) ---- */}
        {isAdmin && (
          <div className="flex flex-col items-center justify-center gap-1 shrink-0 self-center">
            <button
              onClick={handleGenerateCaption}
              disabled={captionData.isLoading || !selectedClient}
              className={[
                "relative p-2 rounded-lg transition-all duration-200",
                "text-gray-400 hover:text-purple-600 hover:bg-purple-50",
                "disabled:opacity-40 disabled:cursor-not-allowed",
              ].join(" ")}
              title={
                generationState?.status === "ready"
                  ? "Caption ready — click to view"
                  : generationState?.status === "generating"
                    ? "Generating caption…"
                    : "Generate caption"
              }
            >
              {generationState?.status === "ready" && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
              )}
              {captionData.isLoading || generationState?.status === "generating" ? (
                <SpinnerIcon className="w-4 h-4" />
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
            </button>

            <button
              onClick={handleEditPost}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
              title="Edit"
            >
              <EditIcon className="w-4 h-4" />
            </button>

            <DeletePostButton post={post} onPostDeleted={onPostDeleted} compact />
          </div>
        )}
      </div>

      {/* Stats panel for narrow widths — moves below the row content
          when there's no horizontal room for the right-side panel. */}
      {isPublishedStatus && (
        <div className="lg:hidden px-4 pb-4 pl-[18px]">
          <PublishedPostStats
            metricsByPlatform={post.metricsByPlatform}
            permalinks={statsPermalinks}
            locale={locale}
            className="!mt-0"
          />
        </div>
      )}

      {/* Caption Generator Modal */}
      <CaptionGeneratorModal
        isOpen={showCaptionModal}
        onClose={() => setShowCaptionModal(false)}
        post={post}
        clientId={selectedClient?.id || ""}
        onCaptionSaved={() => {
          setShowCaptionModal(false);
          if (selectedClient?.id) postCacheUtils.refetchAndUpdatePost(selectedClient.id, post.id);
        }}
      />

      {/* Manual Caption Modal */}
      <ManualCaptionModal
        isOpen={showManualCaptionModal}
        onClose={() => setShowManualCaptionModal(false)}
        post={post}
        clientId={selectedClient?.id || ""}
        initialCaption={post.caption || ""}
        onCaptionSaved={() => {
          setShowManualCaptionModal(false);
          if (selectedClient?.id) postCacheUtils.refetchAndUpdatePost(selectedClient.id, post.id);
        }}
      />
    </div>
  );
});

ListPostCard.displayName = "ListPostCard";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Builds the small uppercase eyebrow line above the caption.
 *
 *  "PUB · 21 MAY 2026 · 5 IMGS"
 *  "PUB · 21 MAY 2026 · 1 VID"
 *
 *  Reads like the journal/ledger date stamps in a Stripe-style
 *  dashboard — frames the row as a record of a past event. */
const EYEBROW_COPY: Record<StatsLocale, {
  pub: string;
  carousel: string;
  imgs: (n: number) => string;
  vids: (n: number) => string;
  bcp47: string;
}> = {
  en: {
    pub: "Pub",
    carousel: "Carousel",
    imgs: (n) => `${n} img${n === 1 ? "" : "s"}`,
    vids: (n) => `${n} vid${n === 1 ? "" : "s"}`,
    bcp47: "en",
  },
  it: {
    pub: "Pubbl.",
    carousel: "Carosello",
    imgs: (n) => `${n} ${n === 1 ? "immagine" : "immagini"}`,
    vids: (n) => `${n} video`,
    bcp47: "it-IT",
  },
};

function buildEyebrow({
  publishedAt,
  isReel,
  isCarousel,
  imageCount,
  locale,
}: {
  publishedAt: string | null | undefined;
  isReel: boolean;
  isCarousel: boolean;
  imageCount: number;
  locale: StatsLocale;
}): string {
  const copy = EYEBROW_COPY[locale];
  const parts: string[] = [];
  if (publishedAt) {
    const d = new Date(publishedAt);
    if (!Number.isNaN(d.getTime())) {
      const day = d.getDate();
      const mon = d.toLocaleString(copy.bcp47, { month: "short" }).toUpperCase();
      const year = d.getFullYear();
      parts.push(`${copy.pub} · ${day} ${mon} ${year}`);
    }
  }
  if (imageCount > 0) {
    if (isReel) {
      parts.push(copy.vids(imageCount));
    } else if (isCarousel) {
      parts.push(`${copy.imgs(imageCount)} · ${copy.carousel}`);
    } else {
      parts.push(copy.imgs(imageCount));
    }
  }
  return parts.join(" · ");
}
